import React, { useState } from 'react';
import { themeColors } from '../../constants/design';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  ShipIcon,
  ChartBarIcon,
  GlobeIcon,
  TrendingUpIcon,
  LightBulbIcon,
  UsersIcon,
  BuildingOfficeIcon,
} from '../icons';
import { FedexporInitiative, PoweredByLytiks } from '../ui/Logo';
import LegalDocumentModal from '../legal/LegalDocumentModal';
import CompanyRegistrationForm from './CompanyRegistrationForm';

const getLoginErrorMessage = (error) => {
  const code = error?.code || '';
  if (['auth/invalid-credential', 'auth/user-not-found', 'auth/wrong-password'].includes(code)) {
    return 'El correo o la contraseña no coinciden. Revisa los datos e intenta nuevamente.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Se bloquearon temporalmente los intentos por seguridad. Espera unos minutos antes de volver a intentar.';
  }
  if (code === 'auth/network-request-failed') {
    return 'No pudimos conectar con el servicio de acceso. Revisa tu conexión e intenta nuevamente.';
  }
  if (code === 'auth/user-disabled') {
    return 'Tu acceso está desactivado. Contacta al administrador.';
  }
  return error?.message || 'No se pudo iniciar sesión. Intenta nuevamente.';
};

const LoginScreen = ({ onLogin, allUsers, authMode = 'mock', onCredentialLogin, onRegister }) => {
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  const demoCompanyUser = allUsers.find(u => u.role === 'company');
  const companyExampleId = authMode === 'firebase' ? 'usuario@empresa.com' : (demoCompanyUser?.id || 'empresa');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (authMode === 'firebase') {
      try {
        await onCredentialLogin({ email: username.trim(), password });
      } catch (loginError) {
        setError(getLoginErrorMessage(loginError));
      } finally {
        setIsLoading(false);
      }
      return;
    }

    let valid = false;
    let user = null;

    if (role === 'admin' && username === 'admin' && password === '123') {
      valid = true;
      user = allUsers.find(u => u.id === 'admin_user');
    } else if (role === 'company') {
      user = allUsers.find(u => u.id === username && u.role === 'company');
      if (user && password === '123') {
        valid = true;
      } else if ((username === 'empresa' || username === 'demo') && password === '123') {
        valid = true;
        user = demoCompanyUser;
      }
    }

    setTimeout(() => {
      if (valid && user) {
        onLogin(user);
      } else {
        setError(`Credenciales incorrectas. Pruebe "admin" / "123" o "${companyExampleId}" / "123".`);
      }
      setIsLoading(false);
    }, 500);
  };

  const RoleButton = ({ value, label, icon, description }) => (
    <button
      onClick={() => {
        if (value === 'public') {
          onLogin({ role: 'public', name: 'Público' });
        } else {
          setRole(value);
        }
      }}
      className="group flex items-center gap-3.5 px-4 py-3 rounded-xl bg-slate-50/70 border border-slate-200 hover:border-[#004AAD]/35 hover:bg-white hover:shadow-sm transition-all duration-200 text-left w-full sm:py-3.5"
    >
      <span className="flex-shrink-0 p-2.5 rounded-lg bg-[#004AAD]/10 text-[#004AAD] transition-colors group-hover:bg-[#004AAD] group-hover:text-white">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        {description && <p className="text-xs text-slate-400 mt-0.5 leading-snug">{description}</p>}
      </div>
      <ChevronRightIcon className="h-4 w-4 text-slate-300 transition-colors group-hover:text-[#004AAD]" />
    </button>
  );

  const heroImage = 'https://res.cloudinary.com/do4vybtt4/image/upload/v1763112644/puerto_maritimo_blog_bardahl_e0r4zf.jpg';

  return (
    <div className="relative flex min-h-[100svh] items-start justify-center overflow-x-hidden bg-[#F4F7FB] text-slate-900 lg:min-h-screen lg:items-center lg:overflow-hidden lg:bg-[#06101f] lg:text-white">
      <div className={`relative mx-auto grid w-full max-w-md gap-3 px-4 py-5 sm:gap-4 sm:px-6 sm:py-9 lg:gap-5 lg:px-6 lg:py-10 ${role === 'register' ? 'lg:max-w-6xl lg:grid-cols-[0.85fr_1.15fr]' : 'lg:max-w-5xl lg:grid-cols-[1.3fr_0.7fr]'}`}>
        <FedexporInitiative className="lg:hidden" />

        {/* Left — Hero Card */}
        <div className="relative hidden min-h-[540px] flex-col overflow-hidden rounded-xl border border-white/[0.08] shadow-sm lg:flex">
          <img src={heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.42]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06101f] via-[#06101f]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06101f]/30 to-transparent" />

          <div className="relative flex flex-col justify-between h-full gap-6 p-6 sm:p-9">
            {/* Top: logo + badge */}
            <div className="flex items-center justify-between">
              <PoweredByLytiks variant="dark" />
              <div className="flex items-center gap-1.5 text-[0.6rem] uppercase text-white/45 border border-white/[0.12] rounded-full px-3 py-1">
                <ShipIcon /> Ecuador
              </div>
            </div>

            {/* Middle: headline */}
            <div className="space-y-3">
              <div className="w-7 h-0.5 bg-[#004AAD] rounded-full" />
              <h1 className="text-[2rem] font-bold leading-snug">
                Percepción ecuatoriana<br />del servicio naviero
              </h1>
              <p className="text-white/55 text-sm leading-relaxed max-w-sm">
                Un espacio colaborativo que monitorea disponibilidad logística, costos y operación portuaria para anticipar riesgos.
              </p>
            </div>

            {/* Features grid */}
            <div className="hidden sm:grid grid-cols-2 gap-2.5">
              {[
                { icon: <ChartBarIcon />, text: 'Dashboards abiertos' },
                { icon: <GlobeIcon />,    text: 'Monitoreo mensual' },
                { icon: <TrendingUpIcon />, text: 'Alertas proactivas' },
                { icon: <LightBulbIcon />, text: 'Insights descargables' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2">
                  <span className="flex-shrink-0 p-1.5 rounded-md bg-white/[0.08] text-white/60">{item.icon}</span>
                  <p className="text-xs text-white/55 leading-tight">{item.text}</p>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => onLogin({ role: 'public', name: 'Público' })}
                className="px-5 py-2.5 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors shadow-sm"
              >
                Ver resultados públicos
              </button>
              <button
                onClick={() => setRole('company')}
                className="px-5 py-2.5 rounded-xl border border-white/25 text-white/75 text-sm font-medium hover:bg-white/[0.07] hover:border-white/40 transition-all"
              >
                Soy una empresa participante
              </button>
            </div>
          </div>
        </div>

        {/* Right — Access Card */}
        <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-[0_18px_50px_rgba(82,105,130,0.16)] lg:shadow-[0_18px_50px_rgba(2,8,23,0.22)]">
          {/* Brand accent bar */}
          <div className="h-[3px] bg-[#004AAD]" />

          <div className="flex-1 flex flex-col p-4 sm:p-6 lg:p-7">
            {/* Access visual */}
            <div className="mb-6 hidden border-b border-slate-100 pb-6 lg:block">
              <FedexporInitiative compact />
            </div>

            {role === 'register' ? (
              <CompanyRegistrationForm onRegister={onRegister} onBack={() => { setRole('company'); setError(''); }} />
            ) : !role ? (
              <div className="flex-1 flex flex-col gap-4 sm:gap-5">
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400 mb-1">Acceso</p>
                  <h2 className="text-xl font-bold text-slate-900">Elige cómo explorar</h2>
                </div>
                <div className="flex flex-col gap-2">
                  <RoleButton value="admin"   label="Administrador"       description="Gestiona envíos, usuarios y publicaciones"   icon={<UsersIcon />} />
                  <RoleButton value="company" label="Empresa exportadora" description="Responde cuestionarios y observaciones"        icon={<BuildingOfficeIcon />} />
                  <RoleButton value="public"  label="Público"             description="Consulta el dashboard abierto"                icon={<ChartBarIcon />} />
                </div>
                {authMode === 'firebase' && (
                  <div className="border-t border-slate-100 pt-4 pb-3">
                    <p className="mb-2 text-xs text-slate-500">¿Tu empresa aún no tiene acceso?</p>
                    <button type="button" onClick={() => setRole('register')} className="w-full rounded-xl border border-[#004AAD]/25 px-4 py-2.5 text-sm font-semibold text-[#004AAD] hover:bg-[#004AAD]/5">Crear cuenta</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                <button
                  onClick={() => { setRole(null); setError(''); setUsername(''); setPassword(''); }}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 mb-5 transition-colors self-start"
                >
                  <ChevronLeftIcon /> Volver
                </button>
                <h2 className="text-xl font-bold text-slate-900 mb-0.5">
                  {role === 'admin' ? 'Administrador' : 'Empresa exportadora'}
                </h2>
                <p className="text-xs text-slate-400 mb-6">Ingresa tus credenciales para continuar</p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  {error && (
                    <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-100">
                      <p className="text-red-600 text-xs leading-relaxed">{error}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-[0.65rem] font-semibold text-slate-400 uppercase mb-1.5">{authMode === 'firebase' ? 'Correo' : 'Usuario'}</label>
                    <input
                      type={authMode === 'firebase' ? 'email' : 'text'}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder={role === 'admin' ? (authMode === 'firebase' ? 'admin@empresa.com' : 'admin') : companyExampleId}
                      autoComplete="username"
                      required
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 focus:border-[#004AAD] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[0.65rem] font-semibold text-slate-400 uppercase mb-1.5">Contraseña</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      required
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 focus:border-[#004AAD] transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || !username.trim() || !password}
                    className={`w-full rounded-xl py-2.5 text-sm text-white font-semibold transition-all mt-1 ${themeColors.primary.bg} ${themeColors.primary.bgHover} ${isLoading || !username.trim() || !password ? 'opacity-60 cursor-not-allowed' : 'shadow-sm hover:shadow-md'}`}
                  >
                    {isLoading ? 'Verificando...' : 'Ingresar'}
                  </button>
                </form>
                {authMode === 'firebase' && role === 'company' && (
                  <button type="button" disabled={isLoading} onClick={() => { setPassword(''); setError(''); setRole('register'); }} className="my-4 text-sm font-semibold text-[#004AAD] hover:underline disabled:opacity-50">Crear cuenta</button>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="mt-auto pt-4 border-t border-slate-100 sm:pt-5">
              <div className="mb-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[0.62rem] font-semibold text-slate-400">
                <button type="button" onClick={() => setLegalModal('privacy')} className="hover:text-[#004AAD]">
                  Privacidad
                </button>
                <span className="text-slate-200">•</span>
                <button type="button" onClick={() => setLegalModal('terms')} className="hover:text-[#004AAD]">
                  Términos de uso
                </button>
              </div>
              <p className="text-[0.58rem] text-slate-300 uppercase text-center">
                Barómetro Naviero Ecuador · 2026
              </p>
              <PoweredByLytiks className="mt-2.5" />
            </div>
          </div>
        </div>

      </div>
      <LegalDocumentModal type={legalModal} onClose={() => setLegalModal(null)} />
    </div>
  );
};

export default LoginScreen;
